/**
 * useViewStatePersist — the S16 "persist" affordance over the ACTIVE view's
 * session-ephemeral state (the view channel of the WYSIWYG split):
 *
 *  - **Drag-pins**: hand placements (the PlacementsProvider session map) write
 *    into the view body as `style <target> { pin x, y }` members — ONE batched
 *    `setPins` mutation through the shared service (engine `upsertPinEdit`
 *    under the hood), so one persist gesture is one generation-guard cycle and
 *    one undo entry. Session placements are kept (they still apply on the
 *    default board); the view text now carries them for everyone.
 *  - **Knob values**: persisting changed knobs writes a view INSTANCE
 *    (`view <Parent> <Name> { knob: value }`) through the engine
 *    `upsertViewInstance` synthesizer (ONE `upsertInstance` mutation — one
 *    guard cycle, one undo entry). Naming rule (prompt-free): when the active
 *    view is ITSELF an instance, its own bindings are upserted in place; when
 *    it is a PARENT, a fresh instance named `<Parent>Pinned` (numeric-suffix
 *    deduped against existing view names) is created. Binding values are
 *    rendered here (grammar-gated: bare identifier/number, else quoted string)
 *    since only the app holds the knob DECLS that fix each value's kind.
 *
 * Null-safe outside the full composition: without a store/placements provider
 * or an active MODEL view (drafts have no text to write into) there is nothing
 * to persist.
 */
import { useMemo } from "react";
import {
  RESERVED_KEYWORDS,
  shortestUniqueSuffix,
  type KnobDecl,
  type StableId,
  type ViewInstanceBinding,
  type ViewPin,
} from "@archlang/engine";
import { useWorkspaceModel } from "../workbench/WorkspaceModelContext.js";
import { useActiveView } from "../workbench/ActiveViewContext.js";
import { usePlacementsContextOptional } from "../workbench/PlacementsContext.js";
import { useModelMutationsOptional, type MutationResult } from "../workbench/mutations.js";
import { useEffectiveViewName } from "./useEffectiveViewName.js";
import { findView, instanceParentOf, viewsByNameOf } from "./viewRepr.js";

export interface ViewStatePersistApi {
  /** The effective MODEL view the persist writes into (null = nothing active). */
  readonly viewName: string | null;
  /** Session hand-placements that would persist as `pin` members. */
  readonly pinCount: number;
  /** Knob names whose live value differs from the view's declared default. */
  readonly changedKnobs: readonly string[];
  /** True: the engine `upsertViewInstance` synthesizer backs the knob channel. */
  readonly knobPersistSupported: boolean;
  /** The instance the knob-persist would write — an existing instance's own
   *  name (update in place) or `<Parent>Pinned` (a fresh create). `null` when
   *  there is nothing to persist. */
  readonly knobTargetName: string | null;
  /** Write every resolvable placement into the view body (one batched
   *  `setPins` op). `null` = nothing to persist / no write surface. */
  persistPins(): MutationResult | null;
  /** Write the changed knob values as a view INSTANCE (one `upsertInstance`
   *  op). `null` = nothing to persist / no write surface. */
  persistKnobs(): MutationResult | null;
}

/** A number knob's declared default, when it has one (other kinds have no
 *  scalar default to compare against — any live value counts as changed). */
function knobDefault(k: KnobDecl): number | string | undefined {
  if (k.kind === "number" && k.default.kind === "number") return k.default.value;
  return undefined;
}

/** A resolved instance binding's scalar value (Value → number | string). A
 *  dotted element binding (`kind: "ref"`) compares as its joined path — the
 *  same string a live element-knob input holds. */
function boundScalar(v: unknown): number | string | undefined {
  const val = v as
    | { kind?: string; value?: number | boolean; text?: string; path?: readonly string[] }
    | undefined;
  if (val?.kind === "number") return val.value as number;
  if (val?.kind === "string" || val?.kind === "identifier") return val.text;
  // A boolean binding (pre-existing text) compares by its literal text — the
  // live inputs hold "true"/"false" as strings.
  if (val?.kind === "boolean") return String(val.value);
  if (val?.kind === "ref" && Array.isArray(val.path)) return val.path.join(".");
  return undefined;
}

/** True when `s` lexes as ONE bare IDENTIFIER token the binding grammar
 *  accepts: the lexer's unicode identifier shape (UAX #31 — ID_Start excludes
 *  a leading underscore) AND not a reserved keyword (`in`, `out`, `one`,
 *  `true`, … lex as keyword tokens, which `parseAtomicValue` rejects bare).
 *  Differs from the engine's `renderValueLiteral` bare class (ASCII-only —
 *  since r37 also single-ident + keyword-rejecting) by admitting the lexer's
 *  full unicode identifier shape. */
const RE_BINDING_IDENT = /^\p{ID_Start}\p{ID_Continue}*$/u;
function isBindingBareIdent(s: string): boolean {
  // No astral chars: the regex matches per CODE POINT but the lexer scans per
  // CODE UNIT (its documented limitation) — an astral ID_Start (𠀀) would
  // pass here yet lex as "Unexpected character". Bare only when the LEXER
  // agrees, i.e. BMP-only (no surrogates).
  return (
    RE_BINDING_IDENT.test(s) &&
    !/[\uD800-\uDFFF]/.test(s) &&
    !RESERVED_KEYWORDS.has(s)
  );
}

/** Render a live knob value as a binding RHS literal the GRAMMAR can express,
 *  or `null` when it can't — the whole persist affordance gates on this (a
 *  null is excluded from `changedKnobs`, never written broken):
 *   - numbers bare;
 *   - element refs: a bare identifier or a DOTTED path of bare identifiers
 *     (`Payments.Api` — parseKnobBinding folds it into a `ref` Value); free
 *     text stays inexpressible (quoting would change its kind);
 *   - everything else: bare when a strict identifier, otherwise QUOTED (a
 *     string literal always parses — the declared enum value "v1.2" IS a
 *     string, so quoting round-trips its semantics). */
function renderKnobLiteral(
  decl: KnobDecl | undefined,
  live: number | string,
): string | null {
  if (typeof live === "number" || decl?.kind === "number") return String(live);
  const s = String(live);
  // Belt for a number-valued ENUM pick that arrived as a string (a <select>
  // yields strings): when the string names one of the enum's declared NUMBER
  // values, write it bare as that number — quoting would flip its kind to
  // string and break `within $knob` on the persisted instance.
  if (decl?.kind === "enum") {
    const n = Number(s);
    if (
      !Number.isNaN(n) &&
      decl.values.some((v) => v.kind === "number" && v.value === n)
    ) {
      return String(n);
    }
  }
  if (decl?.kind === "element") {
    // Dotted path: every segment must lex as a bare IDENTIFIER (the parser
    // consumes path segments with plain `consume("IDENTIFIER")` — keyword
    // segments don't parse there either).
    const segs = s.split(".");
    return segs.length > 0 && segs.every(isBindingBareIdent) ? s : null;
  }
  return isBindingBareIdent(s) ? s : JSON.stringify(s);
}

const EMPTY_KNOBS: Readonly<Record<string, number | string>> = {};

export function useViewStatePersist(): ViewStatePersistApi {
  const { resolvedModel, graph } = useWorkspaceModel();
  const { knobsByView, draftView } = useActiveView();
  const placements = usePlacementsContextOptional();
  const mutations = useModelMutationsOptional();
  const effectiveName = useEffectiveViewName();
  // Only THIS view's live knob values count as persistable changes — the
  // session map is per-view keyed (same keying as the projection). EMPTY is a
  // module constant so the memo below keys on a stable identity.
  const knobValues = (effectiveName ? knobsByView[effectiveName] : undefined) ?? EMPTY_KNOBS;

  return useMemo<ViewStatePersistApi>(() => {
    // While a builder DRAFT projects, the persist channel parks entirely (the
    // same gate as useBoardEditing's view channel): the board renders the
    // draft's `::draft`-keyed state, so persisting the named view's stale
    // values would write text that contradicts what's on screen.
    // Qualified-first resolution (findView) — a module-scoped view is
    // addressed by its qualified name (`Payments.Deep`); a bare-name-only
    // lookup left the persist affordance silently dead for it.
    const view =
      effectiveName && resolvedModel && !draftView
        ? findView(resolvedModel.views, effectiveName)
        : undefined;
    // Address mutations by the name the view was RESOLVED under (qualified for
    // a module-scoped view) — `view.name` alone would mis-address a shadowed
    // bare name.
    const viewName = view ? effectiveName : null;

    // Resolve each placement's board id to a qualified module path, then to
    // the minimal unique reference the style-clause subject uses (§4.11).
    const allPaths = graph ? [...graph.pathOfModule.values()] : [];
    const pins: ViewPin[] = [];
    if (graph && placements) {
      for (const [id, bounds] of placements.placements) {
        const raw = id.startsWith("#") ? id.slice(1) : id;
        const byId = graph.modulesById.get(raw as StableId);
        const path = byId
          ? graph.pathOfModule.get(byId)
          : allPaths.find((p) => p.join(".") === raw);
        if (!path) continue; // non-module / unknown override — nothing to pin
        pins.push({
          targetName: shortestUniqueSuffix(allPaths, path),
          x: Math.round(bounds.x),
          y: Math.round(bounds.y),
        });
      }
    }

    // Knob DECLS live on the PARENT view; an instance carries only bindings, so
    // resolve the parent when the active view is itself an instance —
    // via instanceParentOf, the projection's own resolution (findView's
    // qualified-first pick could land on a different twin than the rendered
    // merge, silently killing the persist affordance — r36).
    const parentView =
      view?.instanceOf && resolvedModel
        ? instanceParentOf(view, resolvedModel.views)
        : view;
    const knobDecls = parentView?.header.knobs ?? [];
    const knobByName = new Map(knobDecls.map((k) => [k.name, k] as const));

    // "Changed" is measured against what the view TEXT already fixes for the
    // knob: an active INSTANCE's own binding when it has one, else the parent's
    // decl default. Against the decl default alone, turning an instance knob TO
    // the default (away from its binding) reads unchanged (unpersistable), and
    // turning it back to its binding value reads changed (a redundant write).
    const boundByName = new Map<string, number | string | undefined>(
      ((view as { bindings?: readonly { name: string; value: unknown }[] } | undefined)
        ?.bindings ?? []).map((b) => [b.name, boundScalar(b.value)] as const),
    );
    const changedKnobs = knobDecls
      .filter((k) => {
        const live = knobValues[k.name];
        if (live === undefined) return false;
        // Only values the binding GRAMMAR can express are persistable — a
        // value renderKnobLiteral can't turn into a parseable RHS (a dotted /
        // free-text element ref) is excluded rather than written broken (the
        // live projection accepts it; the text tier can't, yet).
        if (renderKnobLiteral(k, live) === null) return false;
        const baseline = boundByName.has(k.name) ? boundByName.get(k.name) : knobDefault(k);
        return baseline === undefined || live !== baseline;
      })
      .map((k) => k.name);

    // Prompt-free instance name: an active instance updates itself; a parent
    // spawns `<Parent>Pinned`, numeric-suffix deduped against existing views.
    let knobTargetName: string | null = null;
    if (view && changedKnobs.length > 0) {
      if (view.instanceOf) {
        knobTargetName = view.name;
      } else if (viewsByNameOf(resolvedModel?.views).get(view.name) !== view) {
        // The instance grammar's parent ref is a single BARE identifier
        // resolved first-declaration-wins — under bare-name shadowing the
        // fresh instance would merge the WRONG twin (its clauses + knobs) and
        // the persisted binding would be silently dead. Inexpressible in the
        // text tier → excluded rather than written broken (r47), same posture
        // as the renderKnobLiteral gate above.
        knobTargetName = null;
      } else {
        const taken = new Set((resolvedModel?.views ?? []).map((v) => v.name));
        let candidate = `${view.name}Pinned`;
        for (let n = 2; taken.has(candidate); n++) candidate = `${view.name}Pinned${n}`;
        knobTargetName = candidate;
      }
    }

    return {
      viewName,
      pinCount: pins.length,
      changedKnobs,
      knobPersistSupported: true,
      knobTargetName,
      persistPins() {
        if (!viewName || !mutations || pins.length === 0) return null;
        return mutations.mutateView(viewName, { op: "setPins", pins });
      },
      persistKnobs() {
        if (!view || !viewName || !mutations || !knobTargetName || changedKnobs.length === 0) {
          return null;
        }
        // changedKnobs already filtered out inexpressible values, so the
        // literal is never null here.
        const bindings: ViewInstanceBinding[] = changedKnobs.map((name) => ({
          name,
          value: renderKnobLiteral(knobByName.get(name), knobValues[name]) ?? "",
        }));
        return mutations.mutateView(viewName, {
          op: "upsertInstance",
          instanceName: knobTargetName,
          bindings,
        });
      },
    };
  }, [effectiveName, resolvedModel, graph, placements, knobValues, mutations, draftView]);
}
